import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getCatalogoDiagnosticosControllerCategoriasQueryKey,
  getCatalogoDiagnosticosControllerListarQueryKey,
  getCatalogoDiagnosticosControllerObterQueryKey,
  useCatalogoDiagnosticosControllerAtualizar,
  useCatalogoDiagnosticosControllerCategorias,
  useCatalogoDiagnosticosControllerCriar,
} from '@/api/generated/admin-catalogo-diagnosticos/admin-catalogo-diagnosticos';
import type { CatalogoDiagnosticoResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { ChipsDeSinonimos } from '@/shared/components/ChipsDeSinonimos';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { ComboBox } from '@/shared/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

const SEM_CATEGORIA = '';

const schema = z.object({
  nome: z.string().trim().min(2, 'Informe o nome do diagnóstico').max(200),
  sinonimos: z.array(z.string().trim().min(1).max(120)).max(20, 'No máximo 20 sinônimos'),
  categoria: z.string().trim().max(120),
});

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  diagnostico?: CatalogoDiagnosticoResponse;
}

/**
 * Criar/editar o diagnóstico padronizado.
 *
 * ⚠️ O nome vai POR EXTENSO ("Hiperadrenocorticismo"); a sigla entra como
 * sinônimo. É o sinônimo que a busca casa — quem digita "HAC" só encontra a
 * linha existente se a sigla estiver lá, e quem não encontra cadastra a
 * segunda linha do mesmo diagnóstico.
 */
export function DiagnosticoFormDialogo({ aberto, aoFechar, diagnostico }: Props) {
  const queryClient = useQueryClient();
  const editando = Boolean(diagnostico);

  const criar = useCatalogoDiagnosticosControllerCriar();
  const atualizar = useCatalogoDiagnosticosControllerAtualizar();

  const { data: categorias } = useCatalogoDiagnosticosControllerCategorias({
    query: { enabled: aberto },
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!aberto) return;
    reset({
      nome: diagnostico?.nome ?? '',
      sinonimos: diagnostico ? [...diagnostico.sinonimos] : [],
      categoria: diagnostico?.categoria ?? SEM_CATEGORIA,
    });
  }, [aberto, diagnostico, reset]);

  const sinonimos = watch('sinonimos') ?? [];
  const nome = watch('nome') ?? '';
  const categoria = watch('categoria') ?? SEM_CATEGORIA;

  const opcoesDeCategoria = [
    { valor: SEM_CATEGORIA, rotulo: 'Sem categoria' },
    ...(categorias?.itens ?? []).map((c) => ({ valor: c, rotulo: c })),
  ];

  const aoEnviar = handleSubmit(async (dados) => {
    const corpo = {
      nome: dados.nome,
      sinonimos: dados.sinonimos,
      categoria: dados.categoria.trim() ? dados.categoria.trim() : null,
    };

    try {
      if (diagnostico) {
        await atualizar.mutateAsync({ id: diagnostico.id, data: corpo });
        await queryClient.invalidateQueries({
          queryKey: getCatalogoDiagnosticosControllerObterQueryKey(diagnostico.id),
        });
      } else {
        await criar.mutateAsync({ data: corpo });
      }

      await queryClient.invalidateQueries({
        queryKey: getCatalogoDiagnosticosControllerListarQueryKey(),
      });
      // A categoria recém-digitada precisa aparecer no filtro da listagem.
      await queryClient.invalidateQueries({
        queryKey: getCatalogoDiagnosticosControllerCategoriasQueryKey(),
      });

      toast.success(editando ? 'Diagnóstico atualizado.' : 'Diagnóstico criado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o diagnóstico.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar diagnóstico' : 'Novo diagnóstico'}</DialogTitle>
          <DialogDescription>
            O nome vai por extenso. As siglas entram como sinônimo, que é o que a busca casa.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo id="nome" rotulo="Nome" erro={errors.nome?.message} obrigatorio>
            <Input
              id="nome"
              className={CLASSE_CONTROLE}
              placeholder="Ex.: Hiperadrenocorticismo"
              {...register('nome')}
            />
          </Campo>

          <Campo
            id="sinonimos"
            rotulo="Sinônimos"
            erro={errors.sinonimos?.message}
            dica="Siglas e grafias alternativas. É por elas que “HAC” encontra “Hiperadrenocorticismo”."
          >
            <ChipsDeSinonimos
              id="sinonimos"
              sinonimos={sinonimos}
              nomeDoItem={nome}
              aoMudar={(novos) => setValue('sinonimos', novos, { shouldDirty: true })}
            />
          </Campo>

          <Campo
            id="categoria"
            rotulo="Categoria"
            erro={errors.categoria?.message}
            dica="Só agrupa a listagem e o seletor. Escolha uma existente para não criar duas grafias da mesma categoria."
          >
            <ComboBox
              id="categoria"
              valor={categoria}
              rotuloSelecionado={categoria}
              aoEscolher={(v) => setValue('categoria', v, { shouldDirty: true })}
              opcoes={opcoesDeCategoria}
              placeholder="Sem categoria"
              placeholderBusca="Buscar ou digitar uma categoria..."
              permitirLivre
              rotuloLivre={(termo) => `Criar a categoria ${termo}`}
            />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar diagnóstico'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
